import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { SetContextLink } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { createContext, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { datevServiceUrl, graphqlApiUrl } from '../config';
import { useManagerAuth } from './ManagerAuthContext';

const DatevServiceClientContext = createContext<ApolloClient | undefined>(
  undefined,
);

function buildClient(
  uri: string,
  getAccessToken: () => Promise<string | undefined>,
) {
  const httpLink = new HttpLink({ uri });

  const authLink = new SetContextLink(async ({ headers }) => {
    let accessToken: string | undefined;
    try {
      accessToken = await getAccessToken();
    } catch {
      // no token available, request continues unauthenticated
    }

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 1000,
      jitter: false,
    },
    attempts: {
      max: 3,
      retryIf: (error, _operation) => {
        return !!error;
      },
    },
  });

  return new ApolloClient({
    link: ApolloLink.from([authLink.concat(httpLink), retryLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Zugang: { keyFields: ['uid'] },
      },
    }),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  });
}

export function DatevApolloProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useManagerAuth();

  // Keep the latest getAccessToken without rebuilding the client on every render.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [client] = useState(() =>
    buildClient(graphqlApiUrl, () => getAccessTokenRef.current()),
  );
  const [datevServiceClient] = useState(() =>
    buildClient(datevServiceUrl, () => getAccessTokenRef.current()),
  );

  return (
    <DatevServiceClientContext.Provider value={datevServiceClient}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </DatevServiceClientContext.Provider>
  );
}

/** Apollo client for the DATEV backend service (separate from the manager backend). */
// oxlint-disable-next-line react/only-export-components
export function useDatevServiceClient(): ApolloClient {
  const client = useContext(DatevServiceClientContext);

  if (client === undefined) {
    throw new Error(
      'useDatevServiceClient must be used within a DatevApolloProvider',
    );
  }

  return client;
}
